class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  // ─── OPTIMISTIC UI ───
  // Called from product-form.js BEFORE the fetch. Shows a skeleton row with
  // the product image + title so the user sees immediate feedback. When the
  // POST returns, renderContents() replaces the drawer's inner HTML, which
  // naturally wipes the skeleton.
  showPendingAdd({ image, title, quantity }) {
    CartDrawer.injectPendingStyles();

    // Un-empty the drawer visually so is-empty CSS doesn't hide the contents
    this.classList.remove('is-empty');
    const inner = this.querySelector('.drawer__inner');
    if (inner) inner.classList.remove('is-empty');

    // Hide all stale blocks rendered by Liquid for the "empty cart" state —
    // otherwise the user sees "$45 away from free shipping", empty product
    // table header, etc. during the pending window. renderContents() replaces
    // them with correct populated HTML once the POST returns.
    const staleSelectors = [
      '.drawer__inner-empty',
      '.cart-drawer__shipping',
      '.cart-drawer__discount-nudge',
      '.cart-drawer__table-header',
      'cart-drawer-items',
    ];
    this._hiddenDuringPending = [];
    staleSelectors.forEach((sel) => {
      this.querySelectorAll(sel).forEach((el) => {
        if (el.style.display !== 'none') {
          this._hiddenDuringPending.push(el);
          el.style.display = 'none';
        }
      });
    });

    // Remove any previous pending skeleton (multi-tap guard)
    const existing = this.querySelector('#CartDrawer-Pending');
    if (existing) existing.remove();

    // Build skeleton
    const pending = document.createElement('div');
    pending.id = 'CartDrawer-Pending';
    pending.className = 'cart-drawer__pending';
    const safeTitle = (title || 'Adding to cart…').replace(/[<>&"]/g, (c) => ({'<':'&lt;','>':'&gt;','&':'&amp;','"':'&quot;'}[c]));
    const imgHtml = image
      ? `<img src="${image}" alt="" class="cart-drawer__pending-image" width="60" height="60" loading="eager">`
      : '<div class="cart-drawer__pending-image cart-drawer__pending-image--placeholder"></div>';
    pending.innerHTML = `
      <div class="cart-drawer__pending-row">
        ${imgHtml}
        <div class="cart-drawer__pending-info">
          <div class="cart-drawer__pending-title">${safeTitle}</div>
          <div class="cart-drawer__pending-meta">Qty: ${quantity} · Adding…</div>
        </div>
        <span class="cart-drawer__pending-spinner" aria-hidden="true"></span>
      </div>
    `;

    // Insert at the top of drawer inner (right after the header)
    const header = this.querySelector('.drawer__header');
    if (header && header.parentNode) {
      header.parentNode.insertBefore(pending, header.nextSibling);
    } else if (inner) {
      inner.prepend(pending);
    }

    // Open the drawer now (same logic as open(), but without needing a triggeredBy)
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });
    document.body.classList.add('overflow-hidden');
  }

  clearPending() {
    const p = this.querySelector('#CartDrawer-Pending');
    if (p) p.remove();
  }

  static injectPendingStyles() {
    if (document.getElementById('cart-drawer-pending-styles')) return;
    const style = document.createElement('style');
    style.id = 'cart-drawer-pending-styles';
    style.textContent = `
      .cart-drawer__pending { padding: 12px 16px; background: #faf7ff; border-bottom: 1px solid #eee; }
      .cart-drawer__pending-row { display: flex; align-items: center; gap: 12px; }
      .cart-drawer__pending-image { width: 60px; height: 60px; object-fit: cover; border-radius: 6px; background: #f2f2f2; flex-shrink: 0; }
      .cart-drawer__pending-image--placeholder { background: linear-gradient(90deg,#eee,#f5f5f5,#eee); background-size: 200% 100%; animation: cart-drawer-shimmer 1.2s infinite; }
      .cart-drawer__pending-info { flex: 1; min-width: 0; }
      .cart-drawer__pending-title { font-family: "DM Sans", sans-serif; font-size: 14px; font-weight: 600; color: #1e1e1e; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .cart-drawer__pending-meta { font-family: "DM Sans", sans-serif; font-size: 12px; color: #666; margin-top: 2px; }
      .cart-drawer__pending-spinner { width: 18px; height: 18px; border: 2px solid #e5e5e5; border-top-color: #551A7F; border-radius: 50%; animation: cart-drawer-spin 0.7s linear infinite; flex-shrink: 0; }
      @keyframes cart-drawer-spin { to { transform: rotate(360deg); } }
      @keyframes cart-drawer-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
    `;
    document.head.appendChild(style);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.classList.remove('is-empty');
    const inner = this.querySelector('.drawer__inner');
    if (inner) inner.classList.remove('is-empty');
    const drawerItems = this.querySelector('cart-drawer-items');
    if (drawerItems) drawerItems.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    // Clean up skeleton if still present (defensive — innerHTML swap above
    // should already have removed it since #CartDrawer-Pending lives inside
    // the cart-drawer section that gets replaced).
    this.clearPending();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
